import React from "react";
import useSlugUrl from "~/common/hooks/useSlugUrl";
import { convertToMoney } from "~/common/utils/currency";
import useCollectMetrics from "~/common/hooks/useCollectMetrics";
import { event as gaEvent } from "~/lib/ga";
import Card from "~/components/card";
import ElementsSection from "~/components/element-section";
import { useRouter } from "next/router";
import GoogleAd from "~/common/components/GoogleAd";
import { ADS } from "~/common/constants/googleAds";
import * as DateUtil from "~/common/utils/dates";
import CarView from "~/common/components/CarView/CarView";
import Metadata from "~/common/components/Metadata";
import HttpRequest from "~/common/utils/fetch";
import { messagesByAction, showToast } from "./utils";
import LocalAds from "~/components/local-ads"; // Importación del banner local

export default function CarScreen({
  car: currentCar,
  owner,
  slug,
  recommended,
  financiamientoContact,
  isFinanciable,
  trackEntity,
}) {
  const [car, setCar] = React.useState(() => currentCar);
  const router = useRouter();
  const { collectCarPageView, collectCarView } = useCollectMetrics({ entity: trackEntity });
  const { path } = useSlugUrl({
    id: car.id,
    slug,
  });

  React.useEffect(() => {
    if (car === undefined) return;
    if (car.id === currentCar.id) return;
    setCar(currentCar);
  }, [currentCar, car]);

  React.useEffect(() => {
    collectCarView(car.id)
    collectCarPageView(car.id);

    gaEvent({
      action: "car_view",
      params: {
        id: car.id,
      },
    });
  }, [router, car.id, collectCarPageView]);

  React.useEffect(() => {
    if (car.video) {
      gaEvent({
        action: "car_with_video_view",
        params: {
          id: car.id,
        },
      });
    }
  }, [router, car]);

  async function handleActionDropdownClick(data) {
    const { id, action } = data;
    HttpRequest({
      endpoint: "/api/inventary/" + id + "/status",
      method: "POST",
      data: JSON.stringify({
        action,
      }),
    })
      .then(({ status }) => {
        setCar((prev) => ({
          ...prev,
          status: status.id,
          removedAt: DateUtil.toLocaleDateTime(new Date()),
        }));
        showToast({ message: messagesByAction[action], type: "success" });
      })
      .catch((error) => {
        showToast({ message: messagesByAction[action], type: "error" });
      });
  }

  return (
    <>
      <Metadata
        title={`${car.title} ${car.year} | Seminuevos Sonora`}
        description={`${car.title} ${
          !car.price ? "Negociable" : convertToMoney(Number(car.price))
        }`}
        url={path}
        image={car.thumbnail}
      />
<div className="px-4 md:px-24 lg:px-32 2xl:px-64">
                {<GoogleAd adSlot={ADS.PRINCIPAL_BANNER} />}
      </div>

      {/* Estructura dividida: Fila en pantallas grandes y Columna en móviles */}
      <div className="flex flex-col lg:flex-row w-full gap-4 mt-4">
        
        {/* Columna Izquierda (Ficha técnica + Bloques de recomendados) */}
        <div className="w-full lg:w-10/12 flex flex-col">
          <CarView
            trackEntity={trackEntity}
            car={car}
            owner={owner}
            slug={slug}
            isFinanciable={isFinanciable}
            financiamientoContact={financiamientoContact}
            onActionDropdownClick={handleActionDropdownClick}
          />
          <div className="pt-8">
{/* px-4 da padding en ambos lados para móvil. Luego controlamos cada lado por separado en web */}
            <div className="px-4 md:pl-24 md:pr-4 lg:pl-32 lg:pr-8 2xl:pl-64 2xl:pr-8">
                                <ElementsSection
                title={`Otros ${car.title}`}
                more={{
                  text: "Ver mas",
                  url: `/autos`,
                }}
              >
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {recommended.byModel.map((car) => {
                    const metas = [
                      {
                        value: car.type,
                        link: `/autos/?type=${car.type}`,
                        borderless: false,
                        key: "type",
                      },
                      {
                        value: car.brand,
                        link: `/autos/?brand=${car.brand}`,
                        borderless: false,
                        key: "brand",
                      },
                      {
                        value: car.year,
                        link: `/autos/?year=${car.year}`,
                        borderless: false,
                        key: "year",
                      },
                      {
                        value: car.transmission,
                        link: `/autos/?transmission=${car.transmission}`,
                        borderless: false,
                        key: "transmission",
                      },
                      {
                        value: car.city,
                        link: `/autos/?city=${car.city}`,
                        borderless: false,
                        key: "city",
                      },
                      {
                        value: car.state,
                        link: `/autos/?state=${car.state}`,
                        borderless: false,
                        key: "state",
                      },
                    ];
                    return (
                      <Card
                        key={car.id}
                        car={car.title}
                        id={car.id}
                        slug={car.slug}
                        cover={
                          car.cover ||
                          "https://seminuevossonora.mx/inicio/autos/1645116654/1645116654_FIGO-3.jpg"
                        }
                        price={car.price}
                        metas={metas}
                        isRecomended={car.isPremium}
                      />
                    );
                  })}
                </div>
              </ElementsSection>

              <ElementsSection
                title={`Otros ${car.type}`}
                more={{
                  text: "Ver mas",
                  url: `/autos`,
                }}
              >
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {recommended.byType.map((car) => {
                    const metas = [
                      {
                        value: car.brand,
                        link: `/autos/?brand=${car.brand}`,
                        borderless: false,
                        key: "brand",
                      },
                      {
                        value: car.year,
                        link: `/autos/?year=${car.year}`,
                        borderless: false,
                        key: "year",
                      },
                      {
                        value: car.transmission,
                        link: `/autos/?transmission=${car.transmission}`,
                        borderless: false,
                        key: "transmission",
                      },
                      {
                        value: car.city,
                        link: `/autos/?city=${car.city}`,
                        borderless: false,
                        key: "city",
                      },
                      {
                        value: car.state,
                        link: `/autos/?state=${car.state}`,
                        borderless: false,
                        key: "state",
                      },
                    ];
                    return (
                      <Card
                        key={car.id}
                        car={car.title}
                        id={car.id}
                        slug={car.slug}
                        cover={
                          car.cover ||
                          "https://seminuevossonora.mx/inicio/autos/1645116654/1645116654_FIGO-3.jpg"
                        }
                        price={car.price}
                        metas={metas}
                        isRecomended={car.isPremium}
                      />
                    );
                  })}
                </div>
              </ElementsSection>

              {recommended.byFinancimiento.length > 0 && (
                <ElementsSection
                  title={`Otros financiados`}
                  more={{
                    text: "Ver mas",
                    url: `/autos`,
                  }}
                >
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {recommended.byFinancimiento.map((car) => {
                      const metas = [
                        {
                          value: car.brand,
                          link: `/autos/?brand=${car.brand}`,
                          borderless: false,
                          key: "brand",
                        },
                        {
                          value: car.year,
                                                link: `/autos/?year=${car.year}`,
                        borderless: false,
                        key: "year",
                      },
                      {
                        value: car.transmission,
                        link: `/autos/?transmission=${car.transmission}`,
                        borderless: false,
                        key: "transmission",
                      },
                      {
                        value: car.city,
                        link: `/autos/?city=${car.city}`,
                        borderless: false,
                        key: "city",
                      },
                      {
                        value: car.state,
                        link: `/autos/?state=${car.state}`,
                        borderless: false,
                        key: "state",
                      },
                    ];
                    return (
                      <Card
                        key={car.id}
                        car={car.title}
                        id={car.id}
                        slug={car.slug}
                        cover={
                          car.cover ||
                          "https://seminuevossonora.mx/inicio/autos/1645116654/1645116654_FIGO-3.jpg"
                        }
                        price={car.price}
                        metas={metas}
                        isRecomended={car.isPremium}
                      />
                    );
                  })}
                </div>
              </ElementsSection>
            )}
            {/* EL ANUNCIO SE MUEVE AQUÍ ADENTRO, con un margin-top para separarlo de los autos */}
              <div className="mt-8 w-full">
                <GoogleAd adSlot={ADS.HORIZONTAL_AD} />
              </div>
          </div>
        </div>
      </div>

      {/* Columna Derecha: Anuncios (En móvil se manda abajo automáticamente antes del footer) */}
      <div className="w-full lg:w-2/12 pr-4 lg:pr-6 mt-8 lg:mt-0">
        <LocalAds />
      </div>

    </div>
  </>
  );
}